import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CandidateLayout } from '@/components/candidate/CandidateLayout';
import { CommunityHeader } from '@/components/community/CommunityHeader';
import { CommunityBanner } from '@/components/community/CommunityBanner';
import { PinnedRule } from '@/components/community/PinnedRule';
import { CreatePost } from '@/components/community/CreatePost';
import { PostCard } from '@/components/community/PostCard';
import { PostSkeleton } from '@/components/community/PostSkeleton';
import { PostDetailDialog } from '@/components/community/PostDetailDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { Post } from '@/types/community.types';
import { usePageMeta } from '@/hooks/usePageMeta';

const Questions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const postIdFromUrl = searchParams.get('post');
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  usePageMeta({
    title: "Perguntas - Data Hire Club",
    description: "Perguntas da comunidade",
    noindex: true,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const postsPerPage = 10;

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch posts for questions
  const { 
    data: posts = [], 
    isLoading, 
    refetch: refetchPosts 
  } = useQuery({
    queryKey: ['questions-posts', debouncedSearchQuery, page],
    queryFn: async () => {
      console.log('Fetching questions posts...');
      
      let query = supabase
        .from('community_posts')
        .select(`
          id,
          content,
          created_at,
          updated_at,
          likes_count,
          comments_count,
          image_urls,
          gif_url,
          link_url,
          public_profiles!inner(
            id,
            full_name,
            logo_url
          ),
          community_post_likes!left(
            user_id
          )
        `)
        .eq('post_type', 'question')
        .order('created_at', { ascending: false })
        .range((page - 1) * postsPerPage, page * postsPerPage - 1);

      if (debouncedSearchQuery.trim()) {
        query = query.ilike('content', `%${debouncedSearchQuery}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching posts:', error);
        throw error;
      }

      console.log('Fetched posts:', data);

      const { data: { user } } = await supabase.auth.getUser();
      
      const formattedPosts = (data || []).map((post: any) => ({
        id: post.id,
        content: post.content,
        created_at: post.created_at,
        updated_at: post.updated_at,
        likes_count: post.likes_count || 0,
        comments_count: post.comments_count || 0,
        image_urls: Array.isArray(post.image_urls) ? post.image_urls as string[] : [],
        gif_url: post.gif_url || undefined,
        link_url: post.link_url || undefined,
        author: {
          id: post.public_profiles.id,
          full_name: post.public_profiles.full_name,
          logo_url: post.public_profiles.logo_url || ''
        },
        is_liked: user ? post.community_post_likes?.some((like: any) => like.user_id === user.id) : false
      })) as Post[];

      return formattedPosts;
    }
  });

  const handlePostSuccess = async () => {
    await refetchPosts();
  };

  const handlePostUpdate = async () => {
    await refetchPosts();
  };

  const loadMore = async () => {
    setPage(prev => prev + 1);
  };

  // Open dialog when post ID is in URL
  useEffect(() => {
    if (postIdFromUrl && posts.length > 0) {
      const post = posts.find(p => p.id === postIdFromUrl);
      if (post) {
        setSelectedPost(post);
        setDetailDialogOpen(true);
      }
    }
  }, [postIdFromUrl, posts]);

  const handleDialogClose = (open: boolean) => {
    setDetailDialogOpen(open);
    if (!open) {
      setSearchParams({});
      setSelectedPost(null);
    }
  };

  return (
    <CandidateLayout maxWidth="4xl">
      <div className="bg-gray-50 rounded-lg p-6 md:p-8">
        <CommunityHeader 
          title="Carreira & Vagas" 
          description="Discuta sobre oportunidades, transição de carreira e mercado de trabalho em dados"
        />

        <CommunityBanner type="QUESTIONS" />
        
        <PinnedRule type="questions" content="💼 Compartilhe e discuta sobre oportunidades, salários, entrevistas e trajetórias de carreira em dados!" />

        <CreatePost 
          onPostSuccess={handlePostSuccess}
          placeholder="Compartilhe sobre vagas, salários ou faça perguntas sobre carreira..."
        />

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar discussões sobre carreira..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Posts */}
        <div className="space-y-4">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <PostSkeleton key={index} />
            ))
          ) : posts.length > 0 ? (
            <>
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  onLikeChange={handlePostUpdate}
                  onPostDelete={handlePostUpdate}
                />
              ))}
              
              {posts.length === postsPerPage && (
                <div className="text-center py-4">
                  <Button 
                    onClick={loadMore}
                    variant="outline"
                    disabled={isLoading}
                  >
                    Carregar mais
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {debouncedSearchQuery 
                ? 'Nenhum post encontrado com esse termo.'
                : 'Ainda não há posts sobre carreira. Seja o primeiro a compartilhar!'
              }
            </div>
          )}
        </div>
      </div>

      {selectedPost && (
        <PostDetailDialog
          post={selectedPost}
          open={detailDialogOpen}
          onOpenChange={handleDialogClose}
          onLikeChange={handlePostUpdate}
        />
      )}
    </CandidateLayout>
  );
};

export default Questions;
